/**
 * Error Handling Utilities
 */

/**
 * Extract error message from various error types
 */
export const getErrorMessage = (error) => {
  if (!error) return 'An unknown error occurred';

  if (typeof error === 'string') return error;

  if (error.message) return error.message;

  if (error.response?.data?.message) return error.response.data.message;

  if (error.response?.data?.error) return error.response.data.error;

  if (error.response?.status === 401) return 'Unauthorized. Please log in again';

  if (error.response?.status === 403) return 'Forbidden. You do not have permission';

  if (error.response?.status === 404) return 'Resource not found';

  if (error.response?.status === 500) return 'Server error. Please try again later';

  if (error.response?.statusText) return error.response.statusText;

  return 'An unexpected error occurred';
};

/**
 * Extract API error details
 */
export const getApiErrorDetails = (error) => {
  const message = getErrorMessage(error);
  const status = error?.response?.status;
  const code = error?.code;

  return {
    message,
    status,
    code,
    fullError: error,
  };
};

/**
 * Log error to console (development only)
 */
export const logError = (error, context = '') => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[Error${context ? ` - ${context}` : ''}]`, error);
  }
};

/**
 * Create custom error class
 */
export class AppError extends Error {
  constructor(message, statusCode = 500, context = '') {
    super(message);
    this.statusCode = statusCode;
    this.context = context;
    this.name = 'AppError';
  }
}

/**
 * Handle async errors with default error message
 */
export const handleAsyncError = async (asyncFn, errorMessage = null) => {
  try {
    return await asyncFn();
  } catch (error) {
    const message = errorMessage || getErrorMessage(error);
    logError(error, message);
    throw new AppError(message);
  }
};

/**
 * Create API error response handler
 */
export const handleApiError = (error) => {
  const { message, status } = getApiErrorDetails(error);

  if (status === 401) {
    localStorage.removeItem('token');
    window.location.href = '/login';
  }

  return {
    error: true,
    message,
    status,
    data: error.response?.data,
  };
};

/**
 * Retry logic with exponential backoff
 */
export const retryWithBackoff = async (
  fn,
  maxRetries = 3,
  delayMs = 1000
) => {
  let lastError;

  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      if (i < maxRetries - 1) {
        const delay = delayMs * Math.pow(2, i);
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }
  }

  throw lastError;
};

export default {
  getErrorMessage,
  getApiErrorDetails,
  logError,
  AppError,
  handleAsyncError,
  handleApiError,
  retryWithBackoff,
};
